import React from 'react'

import {
  Box,
  Display,
  FlexDirection,
  FontFamily,
  FontWeight,
  GridDisplay,
  Heading1,
  Icon,
  IconVariant,
  Text,
  ZIndex,
} from '@gousto-internal/citrus-react'
import type { CompactCampaignBannerHeaderContentBlock } from '@library/api-menu-experience'

import {
  getBackgroundImageUrl,
  type MerchandisingBannerActionProps,
  ScrollableRail,
} from './campaignBanner.atoms'
import { CompactCampaignBannerRecipeCard } from './CompactCampaignBannerRecipeCard'

type CompactCampaignBannerProps = {
  contentBlock: CompactCampaignBannerHeaderContentBlock
} & MerchandisingBannerActionProps

export const CompactCampaignBanner = ({
  contentBlock,
  getRecipeCardState,
  canAddRecipes,
  reachedLimit,
  onAddClick,
  onBlockedAddAttempt,
  onCtaClick,
  onRecipeClick,
  onRemoveClick,
}: CompactCampaignBannerProps) => {
  const backgroundImageUrl = getBackgroundImageUrl(contentBlock.backgroundImage.crops)

  return (
    <Box
      data-testid="compact-campaign-banner"
      paddingH={[0, 0, 6]}
      paddingBottom={[0, 0, 8]}
      display={GridDisplay.Grid}
      gridTemplateColumns={'fit-content(50%) auto'}
      gridTemplateRows={'auto auto'}
    >
      <Box
        gridColumn={'1 / span 2'}
        gridRow={'1'}
        zIndex={ZIndex.Auto}
        minHeight="100px"
        style={{
          backgroundColor: contentBlock.backgroundColor,
          backgroundImage: backgroundImageUrl
            ? `linear-gradient(135deg, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.2)), url(${backgroundImageUrl})`
            : undefined,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      ></Box>
      <Box
        gridColumn={'1'}
        gridRow={'1'}
        zIndex={ZIndex.Default}
        paddingH={[4, 4, 6]}
        paddingV={[4, 4, 6]}
        paddingRight={[4, 4, 10]}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={2}
        minWidth={'150px'}
      >
        <Heading1
          fontFamily={FontFamily.Bold}
          fontWeight={FontWeight.Bold}
          style={{ color: contentBlock.textColor }}
        >
          {contentBlock.title}
        </Heading1>
        <Text
          style={{
            color: contentBlock.textColor,
          }}
        >
          {contentBlock.description}
        </Text>
        <button
          type="button"
          onClick={() => onCtaClick(contentBlock.targetCategoryId)}
          style={{
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            color: contentBlock.textColor,
            cursor: 'pointer',
            display: 'inline-flex',
            fontSize: '16px',
            fontWeight: FontWeight.Bold,
            gap: '0.25rem',
            padding: '20px 0 0',
          }}
        >
          {contentBlock.ctaText}
          <Icon
            variant={IconVariant.Actionable}
            name="arrow_right"
            style={{ color: contentBlock.textColor }}
          />
        </button>
      </Box>
      <Box
        gridColumn={'2'}
        gridRow={'1 / span 2'}
        zIndex={ZIndex.Default}
        minWidth={'0'}
        paddingV={[4, 4, 6]}
        paddingH={[4, 4, 0]}
        style={{ paddingBottom: 0, paddingRight: 0 }}
      >
        <ScrollableRail gap={2}>
          {contentBlock.items.map((item, index) => {
            const cardState = getRecipeCardState(item.id)

            return (
              <CompactCampaignBannerRecipeCard
                key={item.id}
                recipe={item}
                canAddRecipes={canAddRecipes}
                reachedLimit={reachedLimit}
                onAddClick={onAddClick}
                onBlockedAddAttempt={onBlockedAddAttempt}
                onClick={onRecipeClick}
                onRemoveClick={onRemoveClick}
                index={index + 1}
                isInBasket={cardState.isInBasket}
                isOutOfStock={cardState.isOutOfStock}
              />
            )
          })}
        </ScrollableRail>
      </Box>
    </Box>
  )
}
